import he from 'he';
import { TypeGuard } from './api.types';
import { ADMIN_URL } from '../constants';

// Assertions
export const assertPropertyExists = <T = any>(
    value: any,
    key: string,
    properyParser?: TypeGuard<T>
): T => {
    if (value.hasOwnProperty(key)) {
        return properyParser ? properyParser(value[key]) : value[key];
    }

    throw new Error(`Missing property: "${key}"`);
};

export const assertIsArray = <T = any>(
    value: any,
    itemParser?: TypeGuard<T>
): T[] => {
    if (Array.isArray(value)) {
        return itemParser ? value.map(itemParser) : value;
    }

    throw new Error(`Value is not an iterable object: "${String(value)}"`);
};

// Fallback value wrapper
export const fallbackValue = <T, V>(
    typeGuard: TypeGuard<T>,
    value: V,
): TypeGuard<T | V> => {
    return (raw): T | V => {
        try {
            if (!raw) {
                throw new Error('Empty or non existing value');
            }
            return typeGuard(raw);
        }
        catch (error) {
            return value;
        }
    };
};

// Basic type parsers
export const typeString: TypeGuard<string> = raw => he.decode(String(raw)).trim();
export const typeNumber: TypeGuard<number> = raw => Number(raw);
export const typeArray: TypeGuard<any[]> = raw => String(raw).split(',');
export const typeBoolean: TypeGuard<boolean> = raw => Boolean(raw);

// Composite type parsers
export const typeStringBoolean: TypeGuard<boolean> = raw => {
    const text = String(raw).toLowerCase().trim();

    if (text === 'true' || text === 'yes') {
        return true;
    }
    
    return false;
};

export const typeStringDatetime: TypeGuard<number> = raw => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (typeof raw !== 'string' || !dateRegex.test(raw)) {
        throw new Error('Invalid date format');
    }

    const [year, month, day] = raw.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getTime();
};

export const typeStringAssetPath: TypeGuard<string> = raw => {
    const path = String(raw).trim();

    if (!path.startsWith('/')) {
        throw new Error('Invalid asset path format');
    }

    return ADMIN_URL + path;
};
